<template>
  <div
    class="policy-accident-wrapper"
    :class="[
      `policy-accident-wrapper--${statusColor}`,
      { 'animate__animated animate__zoomOutLeft': isDeleting },
      { deleting: wasDeleted },
      { 'policy-accident-wrapper--open': showChildren },
    ]"
  >
    <div class="policy-accident">
      <div class="policy-accident__row">
        <div
          class="policy-accident__data policy-accident__data--large policy-accident__data--accident-date"
        >
          <div>
            <div>
              {{ userName }}
            </div>
            <div>{{ formatDisplayDate(accident.date) }}</div>
          </div>
          <div class="policy-accident__contact">
            <a
              class="icon icon__phone"
              :href="
                accident.user_id.phone
                  ? `tel:${accident.user_id.phone}`
                  : 'javascript:void(0)'
              "
              :class="{ 'icon__phone--active': accident.user_id.phone }"
            ></a>
            <a
              class="icon icon__mail"
              :href="
                accident.user_id.email
                  ? `mailto:${accident.user_id.email}`
                  : 'javascript:void(0)'
              "
              :class="{ 'icon__mail--active': accident.user_id.email }"
            ></a>
          </div>
        </div>
        <div class="policy-accident__data policy-accident__data--large">
          <EditableComment :comment="accident.comment" :readonly="true" />
        </div>
        <div class="policy-accident__data policy-accident__data--medium">
          {{ price }}
        </div>
        <div class="policy-accident__data policy-accident__data--medium">
          <div class="policy-accident__status-badge"></div>
          <span>{{ accident.accident_status.name }}</span>
        </div>
        <div class="policy-accident__data policy-accident__data--large">
          <div
            class="icon icon__add"
            @click="openAddAccidentModal(accident)"
          ></div>
          <div
            class="icon icon__pdf"
            :class="{ 'icon__pdf--active': files.length }"
            @click="openPolicyFilesModal(accident.id, files, 'accident')"
          ></div>
          <div
            class="icon icon__edit"
            @click="openEditAccidentModal(accident)"
          ></div>
          <div class="icon icon__delete" @click="removeAccident()"></div>
          <div
            class="icon icon__chevron-down-gray"
            :class="{ 'icon__chevron-down-gray--active': showChildren }"
            v-if="accident.childrens.length"
            @click="toggleChildren()"
          ></div>
        </div>
      </div>
    </div>
    <div
      ref="childrenContainer"
      class="policy-accident__children"
      :style="{
        maxHeight: showChildren ? `${containerMaxHeight}px` : '0px',
      }"
    >
      <AccidentChildren
        v-for="accident in accidents"
        :key="accident.id"
        :accident="accident"
        :openEditAccidentModal="openEditAccidentModal"
        :openPolicyFilesModal="openPolicyFilesModal"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import EditableComment from "@/partials/EditableComment";
import AccidentChildren from "@/components/AccountDetails/AccidentChildren";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import statics from "@/statics";

defineOptions({
  name: "Accident",
});

const props = defineProps({
  accident: {
    required: true,
    type: Object,
  },
  openPolicyFilesModal: {
    required: true,
    type: Function,
  },
});

const store = useStore();
const route = useRoute();

// State
const isDeleting = ref(false);
const wasDeleted = ref(false);
const showChildren = ref(false);
const containerMaxHeight = ref(0);
const isMeasuring = ref(false);
const childrenContainer = ref(null);

// Computed
const userName = computed(() =>
  `${props.accident.user_id.first_name} ${props.accident.user_id.last_name}`
);

const statusColor = computed(() =>
  statics.accidentStatuses.find(
    (status) => status.id === props.accident.accident_status.id
  ).name
);

const price = computed(() =>
  props.accident.price ? `${props.accident.price} .-CHF` : statics.empty
);

const files = computed(() => [
  ...props.accident.accident_files,
  ...props.accident.childrens
    .map((accident) => accident.accident_files)
    .flat(),
]);

const accidents = computed(() => {
  if (props.accident.childrens.length) {
    return [...props.accident.childrens].sort((acc, accident) =>
      acc.accident_status.id < accident.accident_status.id ? -1 : 1
    );
  }
  return [];
});

// Watchers
watch(accidents, () => {
  // Recalculate height if children change while expanded
  if (showChildren.value && !isMeasuring.value) {
    updateContainerHeight();
  }
});

// Methods
const openEditAccidentModal = (accident) => {
  store.commit("setAccidentToEdit", accident);
  store.commit("setShowAddAccidentModal", true);
};

const updateContainerHeight = () => {
  if (!childrenContainer.value) return;

  // Measure the actual content height by getting scrollHeight
  const scrollHeight = childrenContainer.value.scrollHeight;

  if (scrollHeight > 0) {
    containerMaxHeight.value = scrollHeight + 20; // Add padding
  } else {
    // If scrollHeight is 0, content might not be rendered yet
    nextTick(() => {
      setTimeout(() => {
        if (childrenContainer.value) {
          containerMaxHeight.value = childrenContainer.value.scrollHeight + 20;
          isMeasuring.value = false;
        }
      }, 100);
    });
  }
};

const toggleChildren = () => {
  showChildren.value = !showChildren.value;

  if (showChildren.value) {
    // Allow children to render first
    nextTick(() => {
      updateContainerHeight();
    });
  } else {
    containerMaxHeight.value = 0;
  }
};

const removeAccident = async () => {
  isDeleting.value = true;
  await store.dispatch("removeAccident", props.accident.id);
  wasDeleted.value = true;
  setTimeout(async () => {
    await store.dispatch("getGlobalCustomer", route.params.id);
  }, 300);
};

const openAddAccidentModal = ({ policy_id, id }) => {
  store.commit("setAccidentPolicyId", policy_id);
  store.commit("setAccidentParentId", id);
  store.commit("setShowAddAccidentModal", true);
};
</script>

<style lang="scss" scoped>
.policy-accident-wrapper--processing {
  background: #7f8fa41a;
}

.policy-accident-wrapper--accepted {
  background: #22db8e1a;
}

.policy-accident-wrapper--declined {
  background: #db22221a;
}

.policy-accident-wrapper {
  margin-bottom: 15px;
  box-shadow: 0px 0px 15px var(--shadow-4);
  position: relative;
  border-radius: var(--radius-1);
}

.policy-accident-wrapper:last-child {
  margin-bottom: 0;
}

.policy-accident-wrapper--open {
  padding: 0 1px 1px 0;
}

.policy-accident-wrapper--processing .policy-accident__status-badge {
  background: #7f8fa4;
}

.policy-accident-wrapper--accepted .policy-accident__status-badge {
  background: #22db8e;
}

.policy-accident-wrapper--declined .policy-accident__status-badge {
  background: #db2222;
}

/* ===== DEEP SELECTORS (Vue 3 SAFE) ===== */

:deep(.policy-accident) {
  height: 66px;
  border: 0;
  z-index: 1;
}

:deep(.policy-accident__data) {
  padding-left: 40px;
  color: var(--text-9);
}

:deep(.policy-accident__data--small) {
  flex: 1;
}

:deep(.policy-accident__data--medium) {
  flex: 2;
}

:deep(.policy-accident__data--large) {
  flex: 3;
}

:deep(.policy-accident__data--accident-date) {
  justify-content: space-between;
}

:deep(.policy-accident__contact) {
  display: flex;
}

:deep(.policy-accident__contact .icon__phone) {
  margin-right: 15px;
}

:deep(.policy-accident__status-badge) {
  height: 16px;
  width: 16px;
  border-radius: 50%;
  margin-right: 10px;
}

:deep(.policy-accident .comment) {
  background: var(--bg-1);
}

:deep(.policy-accident .icon) {
  margin-right: 15px;
}

/* children */
.policy-accident__children {
  overflow: hidden;
  transition: max-height 0.3s ease;
  max-height: 0;
}
</style>